import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { useTradingStore } from './useTradingStore'
import { useAuthStore } from './useAuthStore'

type ConnectionState = 'connected' | 'connecting' | 'disconnected'

interface WebSocketMessage {
  type: string
  data?: any
  timestamp?: string
  [key: string]: any
}

interface WebSocketContextValue {
  isConnected: boolean
  connectionState: ConnectionState
  send: (message: WebSocketMessage) => void
  subscribe: (channels: string[]) => void
  unsubscribe: (channels: string[]) => void
  lastMessage: WebSocketMessage | null
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

const WS_URL = import.meta.env.PROD
  ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/ws`
  : 'ws://localhost:9099/api/ws'

const RECONNECT_DELAY = 3000
const MAX_RECONNECT_ATTEMPTS = 10

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempts = useRef(0)
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout>>()

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected')
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)

  const {
    updateAccountBalance,
    updateBar,
    updateSpread,
    updatePosition,
    updateMetrics,
    updateIndicator,
    addTrade,
    addAlert,
    addLog,
    fetchMetrics,
    fetchChartBars,
    setStrategyStatus,
    setRunMode,
    updateOptimizationProgress,
    setStrategyStartedAt,
  } = useTradingStore()

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) return

    setConnectionState('connecting')

    try {
      // Append auth token as query param if available
      const token = useAuthStore.getState().token
      const wsUrl = token ? `${WS_URL}?token=${encodeURIComponent(token)}` : WS_URL
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('WebSocket connected')
        setConnectionState('connected')
        reconnectAttempts.current = 0

        // Subscribe to all channels by default
        ws.send(JSON.stringify({
          type: 'subscribe',
          channels: ['*']
        }))
        fetchChartBars()
      }

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          setLastMessage(message)
          handleMessage(message)
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e)
        }
      }

      ws.onclose = () => {
        console.log('WebSocket disconnected')
        setConnectionState('disconnected')
        attemptReconnect()
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        ws.close()
      }
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      setConnectionState('disconnected')
      attemptReconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable connect callback; the handlers read refs
  }, [])

  const attemptReconnect = useCallback(() => {
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error('Max reconnection attempts reached')
      return
    }

    reconnectAttempts.current++
    console.log(`Reconnecting... Attempt ${reconnectAttempts.current}`)

    reconnectTimeout.current = setTimeout(() => {
      connect()
    }, RECONNECT_DELAY)
  }, [connect])

  const handleMessage = (message: WebSocketMessage) => {
    switch (message.type) {
      case 'bar':
        if (message.data) updateBar(message.data)
        break

      case 'spread':
        if (message.data) updateSpread(message.data)
        break

      case 'position_update':
        if (message.data) updatePosition(message.data)
        break

      case 'indicator':
        if (message.name && message.data) {
          updateIndicator(message.name, message.data)
        }
        break

      case 'trade_executed':
      case 'order_fill':
        if (message.data) addTrade(message.data)
        break

      case 'alert':
        addAlert({
          level: message.level || 'info',
          message: message.message || '',
          timestamp: message.timestamp || new Date().toISOString()
        })
        break

      case 'account_balance':
        if (message.data) updateAccountBalance(
          message.data.balance,
          message.data.currency,
          message.data.margin_initial ?? 0,
          message.data.margin_maintenance ?? 0,
        )
        break

      case 'metrics':
        if (message.data) updateMetrics(message.data)
        break

      case 'warmup_complete':
        fetchChartBars()
        addLog({
          level: 'info',
          message: `Warmup complete — ${message.data?.bar_count ?? 0} bars loaded`,
          timestamp: message.timestamp || new Date().toISOString(),
          source: 'system'
        })
        break

      case 'heartbeat':
        // Heartbeat received, connection is alive
        break

      case 'connected':
        console.log(`Connected as ${message.client_id}`)
        addLog({
          level: 'info',
          message: `WebSocket connected as ${message.client_id}`,
          timestamp: new Date().toISOString(),
          source: 'system'
        })
        break

      case 'log':
        addLog({
          level: message.level || 'info',
          message: message.message || '',
          timestamp: message.timestamp || new Date().toISOString(),
          source: message.source || 'strategy'
        })
        break

      case 'strategy_started':
        setStrategyStatus('running')
        if (message.data?.mode) {
          setRunMode(message.data.mode === 'live' ? 'live' : 'backtest')
        }
        setStrategyStartedAt(message.data?.started_at ?? new Date().toISOString())
        // Equity curve is server-side history now — intentionally not cleared on start
        useTradingStore.setState((state) => ({
          activeStrategy: state.activeStrategy
            ? { ...state.activeStrategy, is_running: true }
            : null,
        }))
        addLog({
          level: 'info',
          message: `Strategy started: ${message.data?.strategy_id || message.strategy_id || 'unknown'}` +
            (message.data?.preset ? ` (${message.data.preset})` : '') +
            (message.data?.mode ? ` [${message.data.mode}]` : ''),
          timestamp: message.timestamp || new Date().toISOString(),
          source: 'system'
        })
        break

      case 'strategy_stopped':
        setStrategyStatus('stopped')
        setStrategyStartedAt(null)
        useTradingStore.setState((state) => ({
          activeStrategy: state.activeStrategy
            ? { ...state.activeStrategy, is_running: false }
            : null,
        }))
        addLog({
          level: 'info',
          message: `Strategy stopped: ${message.data?.strategy_id || message.strategy_id || 'unknown'}`,
          timestamp: message.timestamp || new Date().toISOString(),
          source: 'system'
        })
        break

      case 'backtest_complete':
        // Backtest finished — update strategy status and refresh metrics
        setStrategyStatus('stopped')
        useTradingStore.setState((state) => ({
          activeStrategy: state.activeStrategy
            ? { ...state.activeStrategy, is_running: false }
            : null,
        }))
        fetchMetrics()

        // Check if this is an optimization result
        if (message.data?.results?.type === 'optimization') {
          useTradingStore.setState({
            optimizationStatus: 'completed',
            optimizationResults: message.data.results.results || [],
            optimizationProgress: {
              current: message.data.results.total_combinations || 0,
              total: message.data.results.total_combinations || 0,
            },
          })
          addLog({
            level: 'info',
            message: `Optimization completed: ${message.data.results.total_combinations} combinations tested`,
            timestamp: new Date().toISOString(),
            source: 'system'
          })
          break
        }

        // Populate Price Chart with backtest bar data if available
        if (message.data?.results?.bars && message.data.results.bars.length > 0) {
          const backtestBars = message.data.results.bars
          useTradingStore.setState({
            bars: backtestBars,
            currentBar: backtestBars[backtestBars.length - 1],
          })
        }

        // Populate trade markers on the chart
        if (message.data?.results?.trades && message.data.results.trades.length > 0) {
          const backtestTrades = message.data.results.trades.map((t: any) => ({
            ...t,
            // Ensure timestamp is in nanoseconds (matching bar timestamp format)
            timestamp: typeof t.timestamp === 'string'
              ? new Date(t.timestamp).getTime() * 1_000_000
              : t.timestamp,
          }))
          useTradingStore.setState({ trades: backtestTrades })
        }

        // Populate alerts from backtest results
        if (message.data?.results?.alerts && message.data.results.alerts.length > 0) {
          const backtestAlerts = message.data.results.alerts.map((a: any) => ({
            level: a.level || 'info',
            message: a.message || '',
            timestamp: a.timestamp || new Date().toISOString(),
          }))
          useTradingStore.setState((state) => ({
            alerts: [...state.alerts, ...backtestAlerts].slice(-50),
          }))
        }

        addLog({
          level: 'info',
          message: `Backtest completed: ${message.data?.backtest_id || 'unknown'}`,
          timestamp: new Date().toISOString(),
          source: 'system'
        })
        break

      case 'optimization_progress':
        if (message.data) {
          updateOptimizationProgress(message.data)
        }
        break

      case 'strategy_changed':
        if (message.data) {
          useTradingStore.setState({
            selectedStrategyId: message.data.strategy_id || null,
            selectedPreset: message.data.preset || null,
          })
          addLog({
            level: 'info',
            message: `Strategy changed: ${message.data.strategy_name || message.data.strategy_id}` +
              (message.data.preset ? ` (${message.data.preset})` : ''),
            timestamp: message.timestamp || new Date().toISOString(),
            source: 'system'
          })
        }
        break

      case 'backtest_error':
        // Also handle optimization errors
        if (useTradingStore.getState().optimizationStatus === 'running') {
          useTradingStore.setState({
            optimizationStatus: 'error',
            optimizationError: message.data?.error || 'Optimization failed',
          })
        }
        addLog({
          level: 'error',
          message: `Backtest failed: ${message.data?.error || 'unknown error'}`,
          timestamp: new Date().toISOString(),
          source: 'system'
        })
        break

      case 'multi_backtest_progress':
        if (message.data) {
          useTradingStore.setState({
            multiBacktestProgress: {
              multi_backtest_id: message.data.multi_backtest_id,
              currentStrategy: message.data.current_strategy || null,
              currentPreset: message.data.current_preset || null,
              currentInstrument: message.data.current_instrument,
              currentIndex: message.data.current_index,
              totalInstruments: message.data.total_instruments,
              runningInstruments: message.data.running_instruments || [],
              completedResults: message.data.completed_results || {},
              errors: message.data.errors || {},
            },
          })
        }
        break

      case 'multi_backtest_complete':
        if (message.data?.multi_backtest_id) {
          useTradingStore.getState().fetchMultiBacktestResults(message.data.multi_backtest_id)
          useTradingStore.setState((state) => ({
            activeStrategy: state.activeStrategy
              ? { ...state.activeStrategy, is_running: false }
              : null,
          }))
          setStrategyStatus('stopped')
          addLog({
            level: 'info',
            message: `Multi-backtest completed: ${message.data.multi_backtest_id}`,
            timestamp: new Date().toISOString(),
            source: 'system',
          })
        }
        break

      case 'data_audit_progress':
        if (message.data) {
          useTradingStore.setState({
            dataAuditProgress: {
              step: message.data.step,
              current: message.data.current,
              total: message.data.total,
            },
          })
        }
        break

      case 'data_audit_complete': {
        const ok = message.data?.status === 'completed'
        useTradingStore.setState({
          dataAuditRunning: false,
          dataAuditProgress: null,
          dataAuditSummary: ok
            ? {
                usable: message.data.usable ?? 0,
                caveat: message.data.caveat ?? 0,
                notSuitable: message.data.not_suitable ?? 0,
                total: message.data.total ?? 0,
              }
            : null,
          dataAuditError: ok ? null : message.data?.error || 'Data-quality audit failed',
        })
        if (ok) {
          // Re-fetch through /backtest/instruments: that is the endpoint that
          // annotates dataQuality from the freshly reloaded module.
          useTradingStore.getState().fetchBacktestInstruments(true)
        }
        addLog({
          level: ok ? 'info' : 'error',
          message: ok
            ? `Data-quality audit completed: ${message.data.usable} usable, ${message.data.caveat} caveat, ${message.data.not_suitable} not suitable`
            : `Data-quality audit failed: ${message.data?.error || 'unknown error'}`,
          timestamp: new Date().toISOString(),
          source: 'system',
        })
        break
      }

      default:
        console.debug('Unhandled message type:', message.type)
    }
  }

  const send = useCallback((message: WebSocketMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket not connected, message not sent')
    }
  }, [])

  const subscribe = useCallback((channels: string[]) => {
    send({ type: 'subscribe', channels })
  }, [send])

  const unsubscribe = useCallback((channels: string[]) => {
    send({ type: 'unsubscribe', channels })
  }, [send])

  useEffect(() => {
    connect()

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current)
      }
      if (wsRef.current) {
        // Prevent onclose from triggering reconnect during cleanup
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [connect])

  return (
    <WebSocketContext.Provider
      value={{
        isConnected: connectionState === 'connected',
        connectionState,
        send,
        subscribe,
        unsubscribe,
        lastMessage,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook lives with its provider by design
export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
